/**
 * Toast Store - Global state management for toasts
 * Uses window object to ensure single instance across HMR
 */

import type { ToastData, ToastVariant } from './types';
import { i18n } from '../../config/i18n';

export interface ToastShowOptions {
  title: string;
  message?: string;
  variant?: ToastVariant;
  duration?: number;
  onClick?: () => void;
}

interface ToastState {
  toasts: ToastData[];
  listeners: Set<(toasts: ToastData[]) => void>;
  idCounter: number;
  subscribe: (listener: (toasts: ToastData[]) => void) => () => void;
  notify: () => void;
  show: (options: ToastShowOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  success: (title: string, message?: string, opts?: Partial<ToastShowOptions>) => string;
  error: (title: string, message?: string, opts?: Partial<ToastShowOptions>) => string;
  warning: (title: string, message?: string, opts?: Partial<ToastShowOptions>) => string;
  info: (title: string, message?: string, opts?: Partial<ToastShowOptions>) => string;
  transactionCompleted: (newBalance: string) => string;
  transactionFailed: (error?: string) => string;
  mintCompleted: () => string;
}

const TOAST_STATE_KEY = '__toastStore__';

// Type-safe window extension for toast store
declare global {
  interface Window {
    [TOAST_STATE_KEY]?: ToastState;
  }
}

function createToastState(): ToastState {
  const state: ToastState = {
    toasts: [],
    listeners: new Set(),
    idCounter: 0,

    subscribe(listener) {
      this.listeners.add(listener);
      listener(this.toasts);
      return () => this.listeners.delete(listener);
    },

    notify() {
      this.listeners.forEach(l => l([...this.toasts]));
    },

    show(options) {
      const toast: ToastData = {
        id: `toast-${++this.idCounter}-${Date.now()}`,
        title: options.title,
        message: options.message,
        variant: options.variant ?? 'info',
        duration: options.duration ?? 5000,
        onClick: options.onClick,
      };
      this.toasts = [toast, ...this.toasts].slice(0, 5);
      this.notify();
      return toast.id;
    },

    dismiss(id) {
      this.toasts = this.toasts.filter(t => t.id !== id);
      this.notify();
    },

    dismissAll() {
      this.toasts = [];
      this.notify();
    },

    success(title, message, opts) {
      return this.show({ title, message, variant: 'success', ...opts });
    },

    error(title, message, opts) {
      return this.show({ title, message, variant: 'error', duration: 7000, ...opts });
    },

    warning(title, message, opts) {
      return this.show({ title, message, variant: 'warning', ...opts });
    },

    info(title, message, opts) {
      return this.show({ title, message, variant: 'info', ...opts });
    },

    transactionCompleted(newBalance) {
      return this.success(i18n.t('toast.transactionConfirmed'), i18n.t('toast.waitingForBalance'), { duration: 6000 });
    },

    transactionFailed(error) {
      return this.error(i18n.t('toast.topUpError'), error || i18n.t('toast.tryLater'), { duration: 8000 });
    },

    mintCompleted() {
      return this.success(i18n.t('toast.numberCreated'), i18n.t('toast.numberWillAppear'), { duration: 6000 });
    },
  };

  return state;
}

function getToastStore(): ToastState {
  if (typeof window === 'undefined') {
    return createToastState();
  }

  if (!window[TOAST_STATE_KEY]) {
    window[TOAST_STATE_KEY] = createToastState();
  }

  return window[TOAST_STATE_KEY]!;
}

export const toastStore = {
  get current() {
    return getToastStore();
  },
  subscribe: (listener: (toasts: ToastData[]) => void) => getToastStore().subscribe(listener),
  show: (options: ToastShowOptions) => getToastStore().show(options),
  dismiss: (id: string) => getToastStore().dismiss(id),
  dismissAll: () => getToastStore().dismissAll(),
  success: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().success(title, message, opts),
  error: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().error(title, message, opts),
  warning: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().warning(title, message, opts),
  info: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().info(title, message, opts),
  transactionCompleted: (newBalance: string) =>
    getToastStore().transactionCompleted(newBalance),
  transactionFailed: (error?: string) =>
    getToastStore().transactionFailed(error),
  mintCompleted: () =>
    getToastStore().mintCompleted(),
};

export const toast = {
  show: (options: ToastShowOptions) => getToastStore().show(options),
  dismiss: (id: string) => getToastStore().dismiss(id),
  dismissAll: () => getToastStore().dismissAll(),
  success: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().success(title, message, opts),
  error: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().error(title, message, opts),
  warning: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().warning(title, message, opts),
  info: (title: string, message?: string, opts?: Partial<ToastShowOptions>) =>
    getToastStore().info(title, message, opts),
  transactionCompleted: (newBalance: string) =>
    getToastStore().transactionCompleted(newBalance),
  transactionFailed: (error?: string) =>
    getToastStore().transactionFailed(error),
  mintCompleted: () =>
    getToastStore().mintCompleted(),
};

